import { useEffect, useRef } from "react";

const DEFAULT_SCROLL_ROOT = ".main__wrapper";

const canAutoLoadMore = (root) => {
  if (!root) {
    return false;
  }

  return root.scrollHeight > root.clientHeight + 1;
};

export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  scrollRootSelector = DEFAULT_SCROLL_ROOT,
  scrollRootRef = null,
  scrollRootElement = null,
  rootMargin = "200px",
}) => {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return undefined;
    }

    const root =
      scrollRootElement ??
      scrollRootRef?.current ??
      document.querySelector(scrollRootSelector);

    if ((scrollRootElement !== null || scrollRootRef) && !root) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || !hasMore || isLoading) {
          return;
        }

        if (!canAutoLoadMore(root)) {
          return;
        }

        onLoadMore();
      },
      {
        root,
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    hasMore,
    isLoading,
    onLoadMore,
    scrollRootSelector,
    scrollRootRef,
    scrollRootElement,
    rootMargin,
  ]);

  return sentinelRef;
};
